import { Form, useActionData, useLoaderData, useNavigation, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { Candidate, Decision } from "@edm/contracts";
import { DraftRowsSkeleton, Loading } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { isApiError, type WorkspaceApi } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Card, DraftIcon, FormError, LinkButton, PageHeader, Skeleton } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

/**
 * Drafts: the `draft` half of the decision state machine.
 *
 * Nobody is waiting on anything here — that is exactly what separates it from
 * Inbox and Reviews, and why this page never nags. Grouped by where a draft came
 * from, because what each one is missing is the opposite: a record read out of
 * the repository needs confirming, while one inferred from code has the
 * structure but no reason (D-0014 leaves `rationale` empty rather than invent one).
 */

type DecisionPayload = {
  source?: string;
  title?: string;
  decisionId?: string;
  path?: string;
  outcome?: string;
  citations?: { evidenceId: string; quote: string }[];
};

async function fallback<T>(p: Promise<T>, value: T): Promise<T> {
  try {
    return await p;
  } catch {
    return value;
  }
}

export function draftsLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  return { drafts: deferred(loadDrafts(context.get(apiContext).workspace(ws.id))) };
}

async function loadDrafts(api: WorkspaceApi) {
  const [candidates, decisions] = await Promise.all([
    fallback(api.candidates({ kind: "decision_candidate", status: "suggested" }), { items: [] as Candidate[], nextCursor: null }),
    fallback(api.listDecisions(), { items: [] as Decision[], nextCursor: null }),
  ]);
  const drafts = decisions.items.filter((d) => d.status === "draft");
  return {
    candidates: candidates.items,
    fromCode: drafts.filter((d) => d.origin === "inferred"),
    fromPeople: drafts.filter((d) => d.origin !== "inferred"),
  };
}

export async function draftsAction({ context, request }: ActionFunctionArgs) {
  const ws = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(ws.id);
  const form = await request.formData();
  const id = String(form.get("id"));
  try {
    if (String(form.get("intent")) === "confirm") await api.confirmCandidate(id);
    else await api.rejectCandidate(id);
    return { ok: true };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

function CandidateRow({ candidate: c, busy }: { candidate: Candidate; busy: boolean }) {
  const { t } = useI18n();
  const p = c.payload as DecisionPayload;
  const inferred = p.source === "inference";
  return (
    <li className="dt-draft">
      <div className="dt-stack dt-gap-1">
        <span className="dt-inline dt-gap-2">
          {p.decisionId && <span className="dt-mono">{p.decisionId}</span>}
          <strong>{p.title ?? c.subject}</strong>
        </span>
        {p.path && <span className="dt-subtle">{t("drafts.quoted", { source: p.path })}</span>}
        {/* An inferred candidate always shows what it read and how sure it is, so
            nobody confirms a claim without seeing what it rests on. */}
        {inferred &&
          p.citations?.slice(0, 2).map((q) => (
            <blockquote key={q.evidenceId} className="dt-quote">
              {q.quote}
            </blockquote>
          ))}
      </div>
      <span className="dt-inline dt-gap-2">
        <Badge tone={inferred ? "warning" : "success"}>{inferred ? t("drafts.confidence", { percent: Math.round(c.confidence * 100) }) : t("drafts.needs.confirm")}</Badge>
        <Form method="post">
          <input type="hidden" name="intent" value="confirm" />
          <input type="hidden" name="id" value={c.id} />
          <Button type="submit" size="sm" variant="secondary" loading={busy}>
            {t("drafts.confirm")}
          </Button>
        </Form>
        <Form method="post">
          <input type="hidden" name="intent" value="reject" />
          <input type="hidden" name="id" value={c.id} />
          <Button type="submit" size="sm" variant="ghost" loading={busy}>
            {t("drafts.reject")}
          </Button>
        </Form>
      </span>
    </li>
  );
}

function DraftRow({ decision: d, base, needsReason }: { decision: Decision; base: string; needsReason: boolean }) {
  const { t } = useI18n();
  return (
    <li className="dt-draft">
      <div className="dt-stack dt-gap-1">
        <span className="dt-inline dt-gap-2">
          <span className="dt-mono">{d.id}</span>
          <strong>{d.title}</strong>
        </span>
        {d.scope && <span className="dt-subtle">{d.scope}</span>}
      </div>
      <span className="dt-inline dt-gap-2">
        {needsReason && !d.rationale && <Badge tone="danger">{t("drafts.needs.reason")}</Badge>}
        <LinkButton to={`${base}/decisions/${d.id}/edit`} size="sm" variant="secondary">
          {t("drafts.edit")}
        </LinkButton>
      </span>
    </li>
  );
}

/** Which groups there are depends on the data; each is a card of rows with their buttons. */
function DraftGroupsSkeleton() {
  const actions = (
    <>
      <Skeleton shape="badge" width={88} />
      <Skeleton shape="button-sm" />
    </>
  );
  return (
    <Loading>
      <Card title={<Skeleton width={200} />} description={<Skeleton width={320} />} padded={false}>
        <DraftRowsSkeleton count={3} actions={actions} />
      </Card>
      <Card title={<Skeleton width={160} />} description={<Skeleton width={280} />} padded={false}>
        <DraftRowsSkeleton count={2} actions={actions} />
      </Card>
    </Loading>
  );
}

export function DraftsSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("drafts.title")} description={t("drafts.description")} />
      <DraftGroupsSkeleton />
    </>
  );
}

export function DraftsRoute() {
  const { drafts } = useLoaderData<typeof draftsLoader>();
  const result = useActionData() as { error?: string } | undefined;
  const { t } = useI18n();

  return (
    <>
      <PageHeader title={t("drafts.title")} description={t("drafts.description")} />
      <FormError message={result?.error} />
      <Deferred data={drafts} fallback={<DraftGroupsSkeleton />}>
        {(data) => <Drafts {...data} />}
      </Deferred>
    </>
  );
}

function Drafts({ candidates, fromCode, fromPeople }: Awaited<ReturnType<typeof loadDrafts>>) {
  const busy = useNavigation().state === "submitting";
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const base = `/w/${workspace.slug}`;
  const total = candidates.length + fromCode.length + fromPeople.length;

  return (
    <>
      {total === 0 ? (
        <EmptyState title={t("drafts.empty.title")} hint={t("drafts.empty.hint")} icon={<DraftIcon size={18} />} />
      ) : (
        <>
          {candidates.length > 0 && (
            <Card title={t("drafts.fromRepository")} description={t("drafts.fromRepository.hint")} padded={false}>
              <ul className="dt-drafts">
                {candidates.map((c) => (
                  <CandidateRow key={c.id} candidate={c} busy={busy} />
                ))}
              </ul>
            </Card>
          )}

          {fromCode.length > 0 && (
            <Card title={t("drafts.fromCode")} description={t("drafts.fromCode.hint")} padded={false}>
              <ul className="dt-drafts">
                {fromCode.map((d) => (
                  <DraftRow key={d.id} decision={d} base={base} needsReason />
                ))}
              </ul>
            </Card>
          )}

          {fromPeople.length > 0 && (
            <Card title={t("drafts.fromPeople")} description={t("drafts.fromPeople.hint")} padded={false}>
              <ul className="dt-drafts">
                {fromPeople.map((d) => (
                  <DraftRow key={d.id} decision={d} base={base} needsReason={false} />
                ))}
              </ul>
            </Card>
          )}

          {fromCode.length > 0 && <p className="dt-subtle">{t("drafts.rationaleEmpty")}</p>}
        </>
      )}
    </>
  );
}
